/**
 * Il gancio che le schermate a valle condividono: leggi una vista, manda un
 * gesto, rileggi.
 *
 * Sta qui e non dentro un'applicazione perché è **il contratto**, non un
 * aspetto: banco e ufficio parlano con le stesse rotte di flusso e devono
 * trattare allo stesso modo un rifiuto, una conferma d'identità e una sessione
 * scaduta. Quello che cambia fra le due è come l'esito si mostra, e quello
 * resta a ciascuna.
 *
 * Gli errori sono **le stesse classi** del trasporto, non omonimi: `esegui`
 * riconosce con `instanceof` i due casi che deve trattare a parte. Un errore
 * costruito qui con `name: 'ConfermaRichiesta'` gli somiglierebbe senza esserlo,
 * e la richiesta della password non si aprirebbe.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { richiesta } from './transport';
import { useSessione } from './provider';
import { chiavi } from './keys';
/**
 * Il client del flusso: `richiesta` con il prefisso delle rotte di reparto.
 *
 * Fino a oggi era una `fetch` sua, con la stessa lettura dei tre esiti copiata
 * riga per riga dal trasporto — 401 che fa scadere la sessione, 403 `conferma`
 * che apre la richiesta della password, 403 e basta che è un no. Due copie di
 * quella lettura vogliono dire che correggerne una lascia l'altra indietro, e
 * l'altra è quella che apre la password: un difetto che si vede solo in
 * reparto. Adesso è una sola, e qui resta la sola differenza vera — il metodo
 * lo decide **la presenza del corpo**, non chi chiama.
 */
async function chiamaFlusso(percorso, token, corpo) {
    return richiesta(`/api/flusso${percorso}`, token, corpo === undefined ? {} : { corpo });
}
/**
 * Legge una vista e manda gesti. `chiave` serve alla cache: due schermate
 * diverse non devono invalidarsi a vicenda senza motivo.
 */
export function useFlusso(chiave, percorso) {
    const { identita, esegui } = useSessione();
    const coda = useQueryClient();
    const [ultimo, setUltimo] = useState(null);
    const vista = useQuery({
        queryKey: chiavi.flusso(chiave),
        queryFn: () => chiamaFlusso(percorso, identita?.token ?? ''),
        refetchInterval: 5000, // il flusso è condiviso: altri reparti lo muovono
    });
    const gesto = useMutation({
        // One gesture or several in a row: the row stops at the first refusal, and
        // that refusal is what the operator reads. Each step goes through `esegui`
        // on its own, so a step that needs the identity confirmation asks for it.
        mutationFn: async (passi) => {
            let esito = null;
            for (const p of passi) {
                esito = await esegui((token) => chiamaFlusso(p.percorso, token, p.corpo ?? {}));
                if (!esito.ok)
                    break;
            }
            if (esito === null)
                throw new Error('Nessun gesto da mandare.');
            return esito;
        },
        onSuccess: (e) => {
            setUltimo(e);
            void coda.invalidateQueries({ queryKey: chiavi.flusso() });
        },
        onError: (e) => setUltimo({ ok: false, titolo: 'Errore', motivo: e.message, regola: '—' }),
    });
    return {
        dati: vista.data,
        caricando: vista.isLoading,
        errore: vista.error,
        ultimo,
        inCorso: gesto.isPending,
        fai: (percorso, corpo) => () => gesto.mutate([{ percorso, corpo }]),
        /**
         * Gestures that only make sense together, sent one after the other: the
         * next leaves only if the previous was accepted.
         */
        faiInOrdine: (...passi) => () => gesto.mutate(passi),
        /**
         * Togliere l'avviso di mezzo.
         *
         * Serve perché le schermate a valle mostrano l'esito nella forma
         * **sospesa** — quella del banco, che galleggia sopra la pagina e porta la
         * X — e una X senza niente da azzerare non è un pulsante. Sta qui e non in
         * ogni schermata perché l'esito lo tiene questo gancio: un `useState` in
         * più per schermata sarebbe un secondo posto da tenere d'accordo con
         * questo, e i due divergerebbero al primo gesto nuovo.
         */
        chiudiUltimo: () => setUltimo(null),
    };
}
//# sourceMappingURL=flow.js.map